import { history } from './history';

export const RETURN_URL_STORAGE_KEY = 'auth.returnUrl';

// Paths a signed-in user may be sent back to. Add a route here when it is wrapped
// with PrivateRoute in App.jsx. Anything else falls back to home.
export const PRIVATE_PATHS = [
    '/',
    '/Dashboard',
    '/ExpenseReimbursementDashboard',
    '/UploadReceipt',
    '/ExpenseReimbursementUploadReceipt',
    '/MileageReimbursementForm',
    '/MileageReimbursementDashboard',
    '/MissingReceiptsDashboard',
    '/ValidationForm',
];

const ALLOWED_PATHS = new Set(PRIVATE_PATHS);

function hasUnsafeChars(value) {
    if (/\s/.test(value)) return true;
    for (let i = 0; i < value.length; i += 1) {
        const code = value.charCodeAt(i);
        if (code < 32 || code === 127) return true;
    }
    return false;
}

function parseReturnUrl(url) {
    if (typeof url !== 'string' || url.length === 0 || url.length > 2048) return null;
    if (hasUnsafeChars(url)) return null;
    if (!url.startsWith('/') || url.startsWith('//') || url.includes('\\')) return null;

    let decoded;
    try {
        decoded = decodeURIComponent(url);
    } catch (e) {
        return null;
    }

    if (hasUnsafeChars(decoded)) return null;
    if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('\\')) return null;
    if (/javascript\s*:/i.test(decoded) || /data\s*:/i.test(decoded)) return null;
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decoded)) return null;

    const hashIndex = decoded.indexOf('#');
    const hash = hashIndex === -1 ? '' : decoded.slice(hashIndex);
    const beforeHash = hashIndex === -1 ? decoded : decoded.slice(0, hashIndex);
    const queryIndex = beforeHash.indexOf('?');
    const search = queryIndex === -1 ? '' : beforeHash.slice(queryIndex);
    let pathname = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);

    if (pathname.includes('//')) return null;
    if (pathname.length > 1 && pathname.endsWith('/')) {
        pathname = pathname.slice(0, -1);
    }
    if (!ALLOWED_PATHS.has(pathname)) return null;

    return { pathname, search, hash };
}

export function toReturnPath(location) {
    if (!location || typeof location !== 'object') return '/';
    const pathname = typeof location.pathname === 'string' && location.pathname ? location.pathname : '/';
    const search = typeof location.search === 'string' ? location.search : '';
    const hash = typeof location.hash === 'string' ? location.hash : '';
    return `${pathname}${search}${hash}`;
}

export function isSafeInternalUrl(url) {
    return parseReturnUrl(url) !== null;
}

export function normalizeReturnUrl(url) {
    const parts = parseReturnUrl(url);
    if (!parts) return null;
    return `${parts.pathname}${parts.search}${parts.hash}`;
}

export function canAccess(pathname, user) {
    if (!user || typeof pathname !== 'string') return false;
    const normalized = pathname.length > 1 && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;
    return ALLOWED_PATHS.has(normalized);
}

export function resolveReturnUrl(candidate, user) {
    const normalized = normalizeReturnUrl(candidate);
    if (!normalized || !user) return '/';
    return normalized;
}

export function rememberReturnUrl(location) {
    const normalized = normalizeReturnUrl(toReturnPath(location));
    if (!normalized) return null;
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, normalized);
    return normalized;
}

export function clearReturnUrl() {
    sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
}

export function readStoredReturnCandidate() {
    const fromState = history.location?.state?.from;
    const stateCandidate = typeof fromState === 'string'
        ? fromState
        : (fromState && typeof fromState === 'object' ? toReturnPath(fromState) : null);
    const normalizedState = stateCandidate ? normalizeReturnUrl(stateCandidate) : null;
    if (normalizedState) return normalizedState;

    return normalizeReturnUrl(sessionStorage.getItem(RETURN_URL_STORAGE_KEY));
}

export function redirectToReturnUrl(user) {
    const target = resolveReturnUrl(readStoredReturnCandidate(), user);
    clearReturnUrl();
    if (history.navigate) {
        history.navigate(target, { replace: true });
    }
    return target;
}
